import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Toast from '../../components/ui/Toast';
import DatePicker from '../../components/ui/DatePicker';
import FileUploader from '../../components/ui/FileUploader';
import qualificationsData from '../../config/allQualifications.json';
import Select from '../../components/ui/Select';
import SkillSelector from '../../components/ui/SkillSelector';
import LocationSelector from '../../components/ui/LocationSelector';
import { useCompany } from '../../context/CompanyContext'; // Import useCompany
import { useAuth } from '../../context/AuthContext'; // Import useAuth
import { JobFormDataType, QualificationUIType } from '../../types/components'; // Import JobFormDataType
import { mapApiCourseToQualification, mapJobFormToApi, mapLocations, mapQualifcations, mapskills, resolveCourseName, resolveQualificationName } from '../../utils/jobPostMapper'; // Import mapJobFormToApi
import { Attachments, CopmanyUser, Currency, EditJobPostRequest, PostSkillsRequest, PostType, QualificationCustomResponse } from '../../types/api'; // Import API types for casting
import { Course} from '../../config/metadata';

const JobForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { postId:rawPostId } = useParams<{ postId?: string }>(); // Get postId from URL for editing
  const postId = rawPostId ? Number(rawPostId) : undefined; // Extract the numeric part of the postId
  const { createJobPost, editJobPost, fetchJobPostById, jobPost, loadingState, errorState, updateJobPostAttachments } = useCompany();
  const { user } = useAuth();
  const [formData, setFormData] = useState<JobFormDataType>({
    // Basic Information
    job_title: '',
    job_description: '',
    job_type: '',
    work_mode: '',
    min_experience: '',
    no_of_openings: 1,
    
    // Salary Information
    min_salary: '',
    max_salary: '',
    salary_currency: 'INR',
    
    // Additional Details
    post_type: 'Immediate',
    expiration_dt: '',
    scheduled_on: '',
    is_draft: false,
    is_active: true,
    
    // Skills and Qualifications (JSON format)
    skills_json: [],
    nice_to_have_skills: [],
    selected_qualifications: [],
    selected_locations: [],
    
    // File attachment
    attachments: []
  });
  const [prefilledFormData , setPrefilledFormData] = useState<JobFormDataType | null>(null)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [toast, setToast] = useState<{message: string; type: 'success' | 'error' | 'warning' | 'info'} | null>(null);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  // Education: sequential multi-select rows
  type EducationRow = { qualificationId: number | null ; courseId: number | null ; customCourse?: string ; allowCustomInput: boolean };
  const [educationRows, setEducationRows] = useState<EducationRow[]>([
    { qualificationId: null, courseId: null, allowCustomInput: false }
  ]);

  // Sort qualifications for display in the specified order
  const sortedQualifications = React.useMemo(() => {
    const desiredOrder = [
      'Doctorate/PhD',
      'Masters/Post-Graduation',
      'Graduation/Diploma',
      '12th',
      '10th',
      'Below 10th',
    ];
    const orderIndex = (name: string) => {
      const idx = desiredOrder.indexOf(name);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };
    return [...(qualificationsData as any[])].sort(
      (a, b) => orderIndex(a.qualificationName) - orderIndex(b.qualificationName)
    );
  }, []);


  const isRowComplete = (row: EducationRow) => {
    if (!row.qualificationId) return false;
    return row.courseId != null;
  };

  // Sync selected_qualifications when rows change
  useEffect(() => {
    // TODO 
    const completed: (QualificationUIType | undefined)[] = educationRows
      .filter(isRowComplete)
      .map((r) => {
        const q = resolveQualificationName(r.qualificationId);
        if (q){
          const c = resolveCourseName(r.qualificationId, r.courseId);
          if (c){
            return {
              qualificationId: q.qualificationId,
              qualificationName: q.qualificationName,
              qualificationType: q.qualificationType,
              course: c,
              customInput : r.customCourse
            }
          }
        }
      });
    const filteredQualifications: QualificationUIType[] = [];
    console.log(completed)
     completed.forEach(a => {
      if (a!= undefined || a!=null) filteredQualifications.push(a);
    })
    setFormData(prev => ({ ...prev, selected_qualifications:filteredQualifications }));
  }, [educationRows]);

  const ensureTrailingEmptyRow = (rows: EducationRow[]): EducationRow[] => {
    const last = rows[rows.length - 1];
    if (isRowComplete(last)) {
      return [...rows, { qualificationId: null, courseId: null, allowCustomInput: false}];
    }
    return rows;
    };

  const updateRow = (index: number, updater: (r: EducationRow) => EducationRow) => {
    setEducationRows((prev) => {
      const copy = prev.map((r, i) => (i === index ? updater(r) : r));
      return ensureTrailingEmptyRow(copy);
    });
  };

  const removeRow = (index: number) => {
    setEducationRows((prev) => {
      console.log("Remove triggered >>", index , prev)
      const copy = prev.filter((_, i) => i !== index);
      if (copy.length === 0) return [{ qualificationId: null, courseId: null, customQualification: '', allowCustomInput: false }];
      return copy;
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const target = e.target as HTMLInputElement;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? target.checked : type === 'file' ? target.files?.[0] || null : value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validation function
  const validateForm = () => {
    const errors: {[key: string]: string} = {};

    // Basic Information validation
    if (!formData.job_title.trim()) errors.job_title = 'Job title is required';
    if (!formData.job_description.trim()) errors.job_description = 'Job description is required';
    if (!formData.job_type) errors.job_type = 'Job type is required';
    if (!formData.work_mode) errors.work_mode = 'Work mode is required';
    if (!formData.min_experience) errors.min_experience = 'Minimum experience is required';
    if (!formData.post_type) errors.post_type = 'Post type is required';

    // Salary validation
    if (!formData.min_salary.trim()) errors.min_salary = 'Minimum salary is required';
    if (!formData.max_salary.trim()) errors.max_salary = 'Maximum salary is required';
    if (formData.min_salary && formData.max_salary && parseFloat(formData.max_salary) < parseFloat(formData.min_salary)) {
      errors.max_salary = 'Maximum salary should be greater than or equal to minimum salary';
    }

    // Date validation
    if (!formData.expiration_dt) errors.expiration_dt = 'Last date to apply is required';
    if (formData.expiration_dt && new Date(formData.expiration_dt) <= new Date()) {
      errors.expiration_dt = 'Last date to apply must be in the future';
    }
    
    // Scheduled On validation - cannot be in the past
    if (formData.scheduled_on && new Date(formData.scheduled_on) < new Date()) {
      errors.scheduled_on = 'Scheduled date cannot be in the past';
    }

    // Skills validation
    if (!formData.skills_json || formData.skills_json.length === 0) {
      errors.skills_json = 'At least one required skill is needed';
    }

    // Location validation
    if (!formData.selected_locations || formData.selected_locations.length === 0) {
      errors.selected_locations = 'At least one location is required';
    }


    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Effect to load job data when editing
  useEffect(() => {
    console.log("Query postId", {postId, jobPost , loadingState})
    if (postId && postId != jobPost?.postId && !loadingState.jobPost) {
      fetchJobPostById(postId);
    }
  }, [postId, jobPost, loadingState.jobPost]);

  // Effect to populate form data once jobPost is fetched
  useEffect(() => {
    try {
      console.log("Setting Form Data useEffect", jobPost , postId , jobPost?.postId)
      if (jobPost && postId && jobPost.postId === postId) {
      // Map jobPost (API type) to formData (component type)
      const prefilledFormData : JobFormDataType = {
        job_title: jobPost.jobTitle || '',
        job_description: jobPost.jobDescription || '',
        job_type: String(jobPost.jobType), // Assuming jobType is enum/number in API, string in form
        work_mode: String(jobPost.workMode), // Assuming workMode is enum/number in API, string in form
        min_experience: String(jobPost.minExperience),
        no_of_openings: jobPost.noOfOpenings || 1,
        min_salary: String(jobPost.minSalary || ''),
        max_salary: String(jobPost.maxSalary || ''),
        salary_currency: jobPost.salaryCurrency || 'INR',
        post_type: jobPost.postType || 'Immediate',
        expiration_dt: jobPost.expirationDt ? new Date(jobPost.expirationDt).toISOString().split('T')[0] : '',
        scheduled_on: jobPost.scheduledOn ? new Date(jobPost.scheduledOn).toISOString().split('T')[0] : '',
        is_draft: jobPost.isDraft || false,
        is_active: jobPost.isActive || false,
        post_status: jobPost.postStatus || 'Draft', // Use postStatus directly
        skills_json: jobPost.skills != undefined ? mapskills(jobPost.skills, 'Required') : [],
        nice_to_have_skills: jobPost.skills != undefined ? mapskills(jobPost.skills, 'Preferred') : [],
        selected_qualifications: mapQualifcations(jobPost.qualifications),
        selected_locations: mapLocations(jobPost.locationIds), // Map back to string array
        attachments: jobPost.attachments || [], // Attachments need separate handling
      }
      setPrefilledFormData(prefilledFormData);
      setFormData(prefilledFormData);
      setEducationRows((prev) => {
        return jobPost.qualifications
          ? [
              ...jobPost.qualifications
                .map((c) => mapApiCourseToQualification(c))
                .filter((q) => q != undefined)
                .map((q) => ({
                  qualificationId: q!.qualificationId,
                  courseId: q!.course.courseId,
                  customCourse: q!.customInput,
                  allowCustomInput: q!.course.allowCustomInput,
                })),
              { qualificationId: null, courseId: null, allowCustomInput: false },
            ]
          : prev;
      });
      // Also need to map qualifications back to educationRows if editing
      // This is complex and might require a separate helper function
    }
    } catch (error) {
      console.error("JobForm err ",error)
    }
    
  }, [jobPost, postId]);

  const handleAttachmentUpload = (newAttachment: Attachments | null) => {
    if (!newAttachment || !newAttachment.typeId) {
      console.error("Uploaded attachment is missing or missing typeId:", newAttachment);
      setToast({ message: "Failed to upload file", type: "error" });
      return;
    }

    // Update companyDetails in context
    updateJobPostAttachments(newAttachment);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      setToast({ message: 'Please fill in all required fields', type: 'error' });
      return;
    }

    if (!user || user.role !== "Company" || !user.companyId) {
      setToast({ message: 'Company not linked or user not found.', type: 'error' });
      return;
    }

    try {
      const apiData = mapJobFormToApi(prefilledFormData, formData, user.userId, postId || 0); // Pass 0 as placeholder for new posts
      
      if (postId) {
        await editJobPost(apiData as EditJobPostRequest);
        if (!errorState.editJob) {
          setToast({ message: 'Job updated successfully', type: 'success' });
        } else {
          setToast({ message: 'Failed to save job post', type: 'error' });
        }
      } else {
        // For new job posts, create a minimal request for ID generation, then edit with full details
        const initialCreateRequest = { companyId: user.companyId, createdBy: user.userId };
        const newPostId = await createJobPost(initialCreateRequest, { ...apiData, postId: 0 } as EditJobPostRequest); // Pass 0 as placeholder for new posts
        navigate(`/company/jobs/${newPostId}/edit`)
        setToast({ message: 'Job posted successfully', type: 'success' });
        // Redirect to dashboard after successful job posting
        setTimeout(() => {
          navigate('/company/dashboard');
        }, 2000);
      }
      setShowSuccessMessage(true);
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to save job post', type: 'error' });
    }
  };

  const handleSaveDraft = async () => {
    if (!user || user.role !== "Company" || !user.companyId) {
      setToast({ message: 'Company not linked or user not found.', type: 'error' });
      return;
    }
    const errors: {[key: string]: string} = {};

    // Basic Information validation
    if (!formData.job_title.trim()) {
      errors.job_title = 'Job title is required';
      setToast({ message: `Job title is required to save as draft, \n you can change this later`, type: 'error' });
      setValidationErrors(errors);
      return;
    }
    try {
      const apiData = mapJobFormToApi(prefilledFormData, { ...formData, is_draft: true }, user.userId, postId || 0); // Pass 0 as placeholder for new drafts
      if (postId) {
        await editJobPost(apiData as EditJobPostRequest);
      } else {
        // For new drafts, create a minimal request for ID generation, then edit with full details
        const initialCreateRequest = { companyId: user.companyId, createdBy: user.userId };
        const newPostId = await createJobPost(initialCreateRequest, { ...apiData, postId: 0 } as EditJobPostRequest); // Pass 0 as placeholder for new drafts
        navigate(`/company/jobs/${newPostId}/edit`)
      }
      setToast({ message: 'Draft saved successfully', type: 'success' });
      setShowSuccessMessage(true);
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to save draft', type: 'error' });
    }
  };

  const isSubmitting = loadingState.createJob || loadingState.editJob;

  return (
    <div className="min-h-screen bg-gray-50">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          position="top-right"
          duration={3000}
          onClose={() => setToast(null)}
          show
        />
      )}
      {/* Header Section */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">{postId ? 'Edit Job Post' : 'Post a New Job'}</h1>
              <p className="text-green-100">Create an attractive job posting to find the best candidates</p>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Quick Tips</p>
                    <p className="text-xs text-green-200">Be specific and clear in your job description</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-green-200">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Basic Information</h2>
                <p className="text-sm text-gray-600">Tell us about the position and requirements</p>
              </div>
            </div>
          </div>
          <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input
                label="Job Title"
                name="job_title"
                value={formData.job_title}
                onChange={handleInputChange}
                placeholder="e.g. Senior React Developer"
                required
              />
              {validationErrors.job_title && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.job_title}</p>
              )}
            </div>
            <div>
              <Select
                label="Job Type"
                options={[
                  { value: '', label: 'Select job type' },
                  { value: 'FullTime', label: 'Full Time' },
                  { value: 'PartTime', label: 'Part Time' },
                  { value: 'Contract', label: 'Contract' },
                  { value: 'Internship', label: 'Internship' },
                  { value: 'Temporary', label: 'Temporary' }
                ]}
                value={formData.job_type}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, job_type: value }));
                  if (validationErrors.job_type) {
                    setValidationErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.job_type;
                      return newErrors;
                    });
                  }
                }}
                required
              />
              {validationErrors.job_type && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.job_type}</p>
              )}
            </div>
            <div>
              <Select
                label="Work Mode"
                options={[
                  { value: '', label: 'Select work mode' },
                  { value: 'Remote', label: 'Remote' },
                  { value: 'Hybrid', label: 'Hybrid' },
                  { value: 'OnSite', label: 'On Site' }
                ]}
                value={formData.work_mode}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, work_mode: value }));
                  if (validationErrors.work_mode) {
                    setValidationErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.work_mode;
                      return newErrors;
                    });
                  }
                }}
                required
              />
              {validationErrors.work_mode && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.work_mode}</p>
              )}
            </div>
            <div>
              <Select
                label="Minimum Experience (Years)"
                options={[
                  { value: '', label: 'Select experience' },
                  { value: '0', label: '0 years (Fresher)' },
                  { value: '1', label: '1 year' },
                  { value: '2', label: '2 years' },
                  { value: '3', label: '3 years' },
                  { value: '4', label: '4 years' },
                  { value: '5', label: '5 years' },
                  { value: '6', label: '6+ years' }
                ]}
                value={formData.min_experience}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, min_experience: value }));
                  if (validationErrors.min_experience) {
                    setValidationErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.min_experience;
                      return newErrors;
                    });
                  }
                }}
                required
              />
              {validationErrors.min_experience && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.min_experience}</p>
              )}
            </div>
            <Input
              label="Number of Openings"
              name="no_of_openings"
              type="number"
              value={formData.no_of_openings}
              onChange={handleInputChange}
              placeholder="1"
              min="1"
              required
            />
            <Select
              label="Post Type"
              options={[
                { value: 'Immediate', label: 'Immediate Hiring' },
                { value: 'Future', label: 'Future Hiring' },
                { value: 'Replacement', label: 'Team Expansion' }
              ] as {value: PostType | string , label: string}[] as any}
              value={formData.post_type}
              onChange={(value) => setFormData(prev => ({ ...prev, post_type: value as PostType }))}
              required
            />
            </div>
          </div>
        </div>

        {/* Salary Information */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-primary-lightGreen/10 to-primary-teaGreen/10 px-6 py-4 border-b border-primary-lightGreen/20">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-primary-lightGreen rounded-xl flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Salary Information</h2>
                <p className="text-sm text-gray-600">Set competitive compensation for your role</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <div className="sm:col-span-1">
                <Input
                  label="Minimum Salary (CTC)"
                  name="min_salary"
                  type="number"
                  value={formData.min_salary}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ 
                      ...prev, 
                      min_salary: value
                    }));
                    
                    // Real-time validation for minimum salary
                    if (value && formData.max_salary && parseFloat(value) > parseFloat(formData.max_salary)) {
                      setValidationErrors(prev => ({
                        ...prev,
                        min_salary: 'Minimum salary should be less than maximum salary'
                      }));
                    } else if (validationErrors.min_salary) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.min_salary;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="e.g. 600000"
                  required
                />
                {validationErrors.min_salary && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.min_salary}</p>
                )}
              </div>
              
              <div className="sm:col-span-1">
                <Input
                  label="Maximum Salary (CTC)"
                  name="max_salary"
                  type="number"
                  value={formData.max_salary}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ 
                      ...prev, 
                      max_salary: value
                    }));
                    
                    // Real-time validation for maximum salary
                    if (value && formData.min_salary && parseFloat(value) < parseFloat(formData.min_salary)) {
                      setValidationErrors(prev => ({
                        ...prev,
                        max_salary: 'Maximum salary should be greater than or equal to minimum salary'
                      }));
                    } else if (validationErrors.max_salary) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.max_salary;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="e.g. 1200000"
                  min={formData.min_salary || undefined}
                  required
                />
                {validationErrors.max_salary && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.max_salary}</p>
                )}
              </div>
              
              <div className="sm:col-span-2 lg:col-span-1">
                <Select
                  label="Currency"
                  options={[
                    { value: 'INR', label: 'INR (₹)' },
                    { value: 'USD', label: 'USD ($)' },
                    { value: 'EUR', label: 'EUR (€)' },
                    { value: 'GBP', label: 'GBP (£)' }
                  ] as {value : Currency , label : string}[] as any}
                  value={formData.salary_currency}
                  onChange={(value) => setFormData(prev => ({ ...prev, salary_currency: value as Currency }))}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* Job Details */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-green-200">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Job Details</h2>
                <p className="text-sm text-gray-600">Describe the role, responsibilities, and requirements</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-6">
            <div className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Job Description *</label>
                <textarea
                  name="job_description"
                  rows={8}
                  value={formData.job_description}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm sm:text-base"
                  placeholder="Describe the role and include key responsibilities, requirements, education requirements, and benefits & perks."
                  required
                />
                {validationErrors.job_description && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.job_description}</p>
                )}
              </div>
              <div>
                <FileUploader
                  label="Job Description Attachment (Optional)"
                  name="jobAttachment"
                  accept=".pdf,.doc,.docx,.txt"
                  maxSize={10 * 1024 * 1024}
                  description="Supported formats: PDF, DOC, DOCX, TXT (Max 10MB)"
                  // currentFile={formData.jobAttachment as any}
                  referenceId={postId?.toString() || ''}
                  attachment={jobPost?.attachments?.find(a=>a.typeId=='JobDescription')}
                  onFileUploaded={handleAttachmentUpload}
                  // onFileSelect={(file) => setFormData(prev => ({ ...prev, jobAttachment: file as any }))}
                  uploadType='JobDescription'
                />
              </div>
            </div>
          </div>
        </div>

		{/* Scheduling & Timeline */}
		<div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-visible">
          <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-green-200">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Scheduling & Timeline</h2>
                <p className="text-sm text-gray-600">Set application deadlines and publishing schedule</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <DatePicker
                  label="Last Date to Apply"
                  value={formData.expiration_dt}
                  onChange={(val) => {
                    if (typeof val == "string") {
                      setFormData((prev) => ({ ...prev, expiration_dt: val }));

                      // Real-time validation for expiration_dt
                      if (val && new Date(val) <= new Date()) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          expiration_dt:
                            "Last date to apply must be in the future",
                        }));
                      } else if (validationErrors.expiration_dt) {
                        setValidationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.expiration_dt;
                          return newErrors;
                        });
                      }
                    }
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
                {validationErrors.expiration_dt && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.expiration_dt}</p>
                )}
              </div>
              <div>
                <DatePicker
                  label="Scheduled On (Optional)"
                  value={formData.scheduled_on}
                  onChange={(val) => {
                    if (typeof val == "string") {
                      setFormData((prev) => ({ ...prev, scheduled_on: val }));

                      // Real-time validation for scheduled_on
                      if (val && new Date(val) < new Date()) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          scheduled_on: "Scheduled date cannot be in the past",
                        }));
                      } else if (validationErrors.scheduled_on) {
                        setValidationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.scheduled_on;
                          return newErrors;
                        });
                      }
                    }
                  }}
                  withTime
                  min={new Date().toISOString().split('T')[0]}
                />
                {validationErrors.scheduled_on && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.scheduled_on}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Leave empty to publish immediately</p>
              </div>
            </div>
          </div>
        </div>

        {/* Skills & Qualifications */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-green-200">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Skills & Qualifications</h2>
                <p className="text-sm text-gray-600">Define required skills and educational background</p>
              </div>
            </div>
          </div>
          <div className="p-6">
          <div className="space-y-6">
            <div>
              <SkillSelector
                label="Required Skills"
                selected={formData.skills_json}
                onChange={(skills) => {
                  setFormData(prev => {
                    const lowerRequired = new Set(skills);
                    const filteredNice = (prev.nice_to_have_skills || []).filter(s => !lowerRequired.has(s));
                    return { ...prev, skills_json: Array.from(new Set(skills)), nice_to_have_skills: filteredNice };
                  });
                  if (validationErrors.skills_json) {
                    setValidationErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.skills_json;
                      return newErrors;
                    });
                  }
                }}
                required
              />
              {validationErrors.skills_json && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.skills_json}</p>
              )}
            </div>
            
            <div>
              <SkillSelector
                label="Good to Have Skills"
                selected={formData.nice_to_have_skills}
                excluded={formData.skills_json}
                onChange={(skills) => {
                  setFormData(prev => {
                    const lowerRequired = new Set((prev.skills_json || []).map(s => s));
                    const uniqueNice = Array.from(new Set(skills)).filter(s => !lowerRequired.has(s));
                    return { ...prev, nice_to_have_skills: uniqueNice };
                  });
                }}
              />
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Education Requirements</label>
              {/* Dynamic rows */}
              <div className="space-y-3">
                {educationRows.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Qualification */}
                    <div className="flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Qualification</label>
                      <Select
                        options={[
                          ...sortedQualifications.map((q:any) => ({ value: String(q.qualificationId), label: q.qualificationName }))
                        ]}
                        value={row.qualificationId ? String(row.qualificationId) : ''}
                        onChange={(value) => {
                          updateRow(idx, (r) => ({
                            ...r,
                            qualificationId: Number(value),
                            courseId: null,
                          }));
                        }}
                      />
                      {row.allowCustomInput && (
                        <input
                          type="text"
                          className="mt-2 w-full px-3 py-2 border rounded-lg"
                          placeholder="Enter your Course name"
                          value={row.customCourse}
                          onChange={(e)=> updateRow(idx, (r)=> ({...r, customCourse: e.target.value != ''? e.target.value : undefined }))}
                        />
                      )}
                    </div>

                    {/* Course */}
                    <div className="flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Course</label>
                      {(
                        <div className="flex items-center gap-2">
                          <Select
                            options={[
                              ...(
                                qualificationsData.find((q:any)=> q.qualificationId === row.qualificationId)?.courses || []
                              ).map((c:any)=> ({ value: String(c.courseId), label: c.courseName }))
                            ]}
                            value={row.courseId ? String(row.courseId) : ''}
                            onChange={(value)=> {
                                updateRow(idx, (r) => {
                                  const course = resolveCourseName(r.qualificationId , Number(value))
                                  return {
                                    ...r,
                                    courseId: Number(value),
                                    allowCustomInput: course? course.allowCustomInput : false
                                  }
                                });
                              }
                            }
                          />
                          {isRowComplete(row) && educationRows.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              className="text-red-600 hover:bg-red-50"
                              iconOnly
                              aria-label="Remove"
                              title="Remove"
                              onClick={() => removeRow(idx)}
                              icon={
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.108 1.022.17m-1.022-.17L18.16 19.673A2.25 2.25 0 0115.917 21.75H8.083a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .567c.34-.062.68-.118 1.022-.17m0 0A48.11 48.11 0 0112 4.5c1.886 0 3.727.128 5.478 .37m-12 .92V3.75A2.25 2.25 0 019.478 1.5h5.044A2.25 2.25 0 0116.772 3.75v1.12" />
                                </svg>
                              }
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Locations */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-green-200">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Job Locations</h2>
                <p className="text-sm text-gray-600">Select where this position is available</p>
              </div>
            </div>
          </div>
          <div className="p-6">
          <div>
            <LocationSelector
              label="Select Locations"
              selected={formData.selected_locations}
              onChange={(locations) => {
                setFormData(prev => ({ ...prev, selected_locations: locations }));
                if (validationErrors.selected_locations) {
                  setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.selected_locations;
                    return newErrors;
                  });
                }
              }}
              required
            />
            {validationErrors.selected_locations && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.selected_locations}</p>
            )}
          </div>
          </div>
          </div>



        {showSuccessMessage && (
          <div className="bg-white rounded-2xl shadow-lg border border-green-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-green-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-green-900">{formData.is_draft ? 'Draft Saved' : 'Job Posted Successfully!'}</h3>
                  <p className="text-sm text-green-700">{formData.is_draft ? 'Your draft has been saved. You can publish it later from the job list.' : 'Your job posting is now live and accepting applications'}</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-sm text-green-800">
                  {formData.is_draft
                    ? "Your job has been saved as a draft. Edit and publish it anytime from your job list."
                    : "Your job posting has been published and is now visible to job seekers. You'll start receiving applications and can manage them from your job list."}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 sm:space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/company/dashboard')}
              className="w-full sm:w-auto px-8 py-3 border-gray-300 text-gray-700 hover:bg-gray-50"
          >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            Cancel
          </Button>
            
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                loading={isSubmitting}
                disabled={!(jobPost?.postStatus == undefined || jobPost?.postStatus == 'Draft')}
                className="w-full sm:w-auto px-8 py-3 border-[#87DE88] text-green-700 hover:bg-gray-50 hover:border-[#87DE88]"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Save Draft
              </Button>
              
              {location.pathname.includes('/create') ? null : (
                <Button 
                  type="submit" 
                  loading={isSubmitting}
                  className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg hover:shadow-xl transition-all duration-200"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, is_draft: false }));
                  }}
                >
                  {/* icon removed as requested */}
                  Publish Job
                </Button>
              )}
            </div>
          </div>
        </div>
      </form>
      </div>
    </div>
  );
};

export default JobForm;
